import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { ActionByType, ProfileDocument } from "../../common/ProfileDocument";
import { staggeredBaseQuery } from "./staggeredBaseQuery";

export const profileDocumentApi = createApi({
  reducerPath: "profileDocumentApi",
  tagTypes: ["ProfileDocument"],
  baseQuery: staggeredBaseQuery(""),
  endpoints: (builder) => ({
    getProfileDocuments: builder.query<ProfileDocument[], number>({
      query: (profileId) => {
        return {
          method: "GET",
          url: `profileDocuments?profileId=${profileId}`,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "ProfileDocument" as const,
                id,
              })),
              { type: "ProfileDocument", id: "LIST" },
            ]
          : [{ type: "ProfileDocument", id: "LIST" }],
    }),
    getProfileDocument: builder.query<ProfileDocument, String>({
      query: (profileDocumentId) => {
        return {
          method: "GET",
          url: `profileDocuments/${profileDocumentId}`,
        };
      },
    }),
    downloadProfileDocument: builder.mutation<Blob, { fileKey: String }>({
      query: ({fileKey}) => {
        fileKey = fileKey.replaceAll('[', '%5B').replaceAll(']', '%5D');
        return {
          method: 'GET',
          url: `${fileKey.charAt(0) === '/' ? fileKey : `/${fileKey}`}`,
          responseHandler: (response) => response.blob()
        };
      },
    }),
    uploadProfileDocument: builder.mutation<ProfileDocument, FormData>({
      query: (data) => {
        return {
          method: "POST",
          url: "profileDocuments/",
          body: data,
        };
      },
      invalidatesTags: [{ type: "ProfileDocument", id: "LIST" }],
    }),
    updateProfileDocument: builder.mutation<
      ProfileDocument,
      { profileDocumentId: String; profileDocument: Partial<ProfileDocument> }
    >({
      query: ({ profileDocumentId, profileDocument }) => {
        return {
          method: "PUT",
          url: `profileDocuments/${profileDocumentId}`,
          body: profileDocument,
        };
      },
      invalidatesTags: [{ type: "ProfileDocument", id: "LIST" }],
    }),
    deleteProfileDocument: builder.mutation<void, {profileDocumentId: String, actionByType: ActionByType}>({
      query: ({profileDocumentId, actionByType}) => {
        return {
          method: "DELETE",
          url: `profileDocuments?id=${profileDocumentId}&actionByType=${actionByType}`,
        };
      },
      invalidatesTags: (result, error, profileDocumentId) => [
        { type: "ProfileDocument", profileDocumentId },
      ],
    }),
  }),
});

export const {
  useGetProfileDocumentsQuery,
  useGetProfileDocumentQuery,
  useDownloadProfileDocumentMutation,
  useUploadProfileDocumentMutation,
  useUpdateProfileDocumentMutation,
  useDeleteProfileDocumentMutation,
} = profileDocumentApi;
